import { PlusIcon, TrashIcon } from '@heroicons/react/24/outline'
import { GlobalConfigType, setGlobalConfig } from 'actions'
import cloneDeep from 'clone-deep'
import { OptionModal } from 'components/Modals/OptionModal'
import { InputType, SettingKey } from 'config'
import { useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Modal } from 'stories/components'
import { InputConvert, InputValidate, thousandSeperator } from 'utils'
import { getOptionValues, IOptionType } from 'utils/general'
import { RenderInput } from 'utils/RenderInput'

import {
  AccountTransactionStatus,
  AccountTransactionStatusOptions,
  AccountTransactionType,
  AccountTransactionTypeOptions,
  IAccountTransaction,
  IDistributionLine,
} from './types'

const BALANCE_TOLERANCE = 0.01

const emptyLine: IDistributionLine = {
  account: '',
  debit: '',
  credit: '',
  description: '',
  class: '',
}

const todayIso = () => new Date().toISOString().slice(0, 10)

const round2 = (n: number) => Math.round(n * 100) / 100

/** Header metadata only — account + amounts live in the Lines grid. */
export const defaultInputs = (): Record<string, InputType> => {
  return {
    date: {
      inputType: 'text',
      type: 'date',
      title: 'Date',
      required: true,
    },
    type: {
      inputType: 'select',
      options: AccountTransactionTypeOptions,
      title: 'Type',
      required: false,
      hasDefaultOption: true,
    },
    refNo: {
      inputType: 'text',
      title: 'Ref No',
      required: false,
    },
    loanNumber: {
      inputType: 'text',
      type: 'number',
      title: 'Loan Number',
      required: false,
    },
    status: {
      inputType: 'select',
      options: AccountTransactionStatusOptions,
      title: 'Status',
      required: false,
    },
    payee: {
      inputType: 'select',
      title: 'Payee',
      required: false,
      hasDefaultOption: true,
      defaultOptionText: 'Select or add…',
      options: [],
    },
    class: {
      inputType: 'text',
      title: 'Class',
      required: false,
    },
    location: {
      inputType: 'text',
      title: 'Location',
      required: false,
    },
    memo: {
      inputType: 'textarea',
      title: 'Memo',
      required: false,
      span: 'full',
    },
  }
}

const linesFromItem = (item: IAccountTransaction | null, seedAccountNo?: number): IDistributionLine[] => {
  if (item) {
    const header: IDistributionLine = {
      account: item.accountNo != null ? String(item.accountNo) : '',
      debit: item.payment != null && Number(item.payment) !== 0 ? String(item.payment) : '',
      credit: item.deposit != null && Number(item.deposit) !== 0 ? String(item.deposit) : '',
      description: item.memo || '',
      class: item.class || '',
    }

    if (item.distributionLines && Array.isArray(item.distributionLines) && item.distributionLines.length > 0) {
      return [header, ...item.distributionLines.map((l) => ({ ...emptyLine, ...l, account: String(l.account || '') }))]
    }

    if (item.account != null && Number(item.account) !== 0) {
      return [
        header,
        {
          account: String(item.account),
          debit: item.deposit != null && Number(item.deposit) !== 0 ? String(item.deposit) : '',
          credit: item.payment != null && Number(item.payment) !== 0 ? String(item.payment) : '',
          description: item.memo || '',
          class: item.class || '',
        },
      ]
    }

    return [header, { ...emptyLine }]
  }

  // Create: register seeds line 1; GL leaves it blank (optional filter prefill via seedAccountNo).
  return [
    { ...emptyLine, account: seedAccountNo != null && seedAccountNo > 0 ? String(seedAccountNo) : '' },
    { ...emptyLine },
  ]
}

export const EditAccountTransactionDialog = ({
  item,
  onClose,
  accountNo,
  allAccounts,
  openedFromLedger = false,
  suggestedRefNo,
}: {
  item: IAccountTransaction | null
  onClose: Function
  /** When set (register or GL filter), seeds line 1. When omitted, Account is picked in the grid. */
  accountNo?: number
  allAccounts: any[]
  /** GL entry point: default Type to Journal Entry and label the modal accordingly. */
  openedFromLedger?: boolean
  /** Suggested Ref No (e.g. JE-0024) when creating from the ledger. */
  suggestedRefNo?: string
}) => {
  const dispatch = useDispatch()
  const payeeOptions = useSelector((state: any) => state.globalConfigReducer[GlobalConfigType.ACCOUNT_PAYEES] || [])

  const [isLoading, setLoading] = useState(false)
  const [payeeModal, setPayeeModal] = useState<SettingKey | null>(null)
  const [lines, setLines] = useState<IDistributionLine[]>([{ ...emptyLine }, { ...emptyLine }])
  const [lineError, setLineError] = useState<string | null>(null)
  const [inputs, setInputs] = useState(defaultInputs())

  const accountOptions: Record<string, string> = {}
  allAccounts.forEach((acc) => {
    accountOptions[acc.no.toString()] = `${acc.name} (${acc.number})`
  })

  const amountsLocked = inputs.status?.value == AccountTransactionStatus.RECONCILED

  useEffect(() => {
    const newInputs = cloneDeep(defaultInputs())

    if (item) {
      Object.keys(newInputs).forEach((key) => {
        if ((item as any)[key] !== undefined) {
          newInputs[key].value = (item as any)[key]
        }
      })
    } else {
      newInputs.date.value = todayIso()
      newInputs.status.value = AccountTransactionStatus.PENDING
      // Ledger posting defaults to Journal Entry; register leaves Type unset.
      if (openedFromLedger) {
        newInputs.type.value = AccountTransactionType.JOURNAL_ENTRY
        newInputs.type.required = true
        newInputs.memo.required = true
      }
      if (suggestedRefNo) {
        newInputs.refNo.value = suggestedRefNo
      }
    }

    if (newInputs.type.value === AccountTransactionType.JOURNAL_ENTRY) {
      newInputs.memo.required = true
    }

    setLines(linesFromItem(item, accountNo))
    setLineError(null)
    setInputs(newInputs)
  }, [item, allAccounts, accountNo, openedFromLedger, suggestedRefNo])

  const getTotals = (rows: IDistributionLine[] = lines) => {
    let totalDebit = 0
    let totalCredit = 0
    rows.forEach((line) => {
      totalDebit += Number(line.debit) || 0
      totalCredit += Number(line.credit) || 0
    })
    return { totalDebit: round2(totalDebit), totalCredit: round2(totalCredit) }
  }

  const filledLines = useMemo(
    () => lines.filter((line) => line.account || Number(line.debit) || Number(line.credit)),
    [lines],
  )

  const balanceState = useMemo(() => {
    const { totalDebit, totalCredit } = getTotals(filledLines)
    const diff = round2(Math.abs(totalDebit - totalCredit))
    const accounts = filledLines.map((l) => String(l.account)).filter(Boolean)
    const uniqueAccounts = new Set(accounts)
    const hasTwoAccounts = uniqueAccounts.size >= 2 && filledLines.length >= 2
    const eachLineValid = filledLines.every((line) => {
      const d = Number(line.debit) || 0
      const c = Number(line.credit) || 0
      if (!line.account) return false
      if (d > 0 && c > 0) return false
      if (d <= 0 && c <= 0) return false
      return true
    })
    const balanced = diff <= BALANCE_TOLERANCE && totalDebit > 0
    return { totalDebit, totalCredit, diff, balanced, hasTwoAccounts, eachLineValid }
  }, [filledLines])

  const canSubmit =
    balanceState.balanced && balanceState.hasTwoAccounts && balanceState.eachLineValid && !amountsLocked

  const onLineChange = (index: number, field: keyof IDistributionLine, value: string) => {
    setLineError(null)
    const next = cloneDeep(lines)
    next[index][field] = value
    // One side only per line
    if (field === 'debit' && value) next[index].credit = ''
    if (field === 'credit' && value) next[index].debit = ''
    setLines(next)
  }

  const addLine = () => setLines([...lines, { ...emptyLine }])

  const removeLine = (index: number) => {
    if (lines.length <= 2) return
    const next = cloneDeep(lines)
    next.splice(index, 1)
    setLines(next)
  }

  const clearAllLines = () => {
    setLines([
      { ...emptyLine, account: accountNo != null && accountNo > 0 ? String(accountNo) : '' },
      { ...emptyLine },
    ])
  }

  const onChange = (key: string, value: string) => {
    setLineError(null)
    const newInputs = cloneDeep(inputs)
    value = InputConvert(newInputs[key], value)
    newInputs[key].error = InputValidate({ ...newInputs[key], value })
    newInputs[key].value = value
    if (key === 'type') {
      newInputs.memo.required = value === AccountTransactionType.JOURNAL_ENTRY
    }
    setInputs(newInputs)
  }

  const onSubmit = async () => {
    setLineError(null)
    let hasError = false
    const newInputs = cloneDeep(inputs)
    const data: Record<string, any> = {}
    for (const key in inputs) {
      if (newInputs[key].value?.trim) newInputs[key].value = newInputs[key].value.trim()
      newInputs[key].error = InputValidate(newInputs[key])
      data[key] = newInputs[key].value
      if (newInputs[key].error) hasError = true
    }
    if (hasError) {
      setInputs(newInputs)
      return
    }

    const validLines = lines.filter((line) => line.account || Number(line.debit) || Number(line.credit))
    if (validLines.length < 2) {
      setLineError('Add at least two lines with different accounts.')
      return
    }
    for (const line of validLines) {
      const d = Number(line.debit) || 0
      const c = Number(line.credit) || 0
      if (!line.account) {
        setLineError('Each line must have an account.')
        return
      }
      if (d > 0 && c > 0) {
        setLineError('A line cannot have both a debit and a credit.')
        return
      }
      if (d <= 0 && c <= 0) {
        setLineError('Each line must have a debit or a credit amount.')
        return
      }
    }
    const accounts = validLines.map((l) => Number(l.account))
    if (new Set(accounts).size < 2) {
      setLineError('Use at least two different accounts.')
      return
    }
    const totals = getTotals(validLines)
    const diff = round2(Math.abs(totals.totalDebit - totals.totalCredit))
    if (diff > BALANCE_TOLERANCE) {
      setLineError(`Out of balance by $${diff.toFixed(2)}. Total debits must equal total credits.`)
      return
    }
    if (totals.totalDebit <= 0) {
      setLineError('Enter non-zero amounts.')
      return
    }

    // Payload contract unchanged: line 1 → header; lines 2–n → distributionLines (opposite side of header).
    const [header, ...rest] = validLines
    const headerDebit = Number(header.debit) || 0
    const headerCredit = Number(header.credit) || 0

    setLoading(true)
    const submitData = {
      id: item ? item.id : -Date.now(),
      ...data,
      accountNo: Number(header.account),
      payment: headerDebit,
      deposit: headerCredit,
      account: rest[0] ? Number(rest[0].account) : undefined,
      memo: data.memo || header.description || '',
      loanNumber: data.loanNumber !== '' && data.loanNumber != null ? Number(data.loanNumber) : null,
      distributionLines: rest.map((line) => ({
        account: String(line.account),
        debit: line.debit,
        credit: line.credit,
        description: line.description || data.memo || '',
        class: line.class || data.class || '',
      })),
    }

    await onClose(submitData)
    setLoading(false)
  }

  const { totalDebit, totalCredit, diff, balanced } = balanceState
  const title = item ? 'Edit Transaction' : openedFromLedger ? 'New Journal Entry' : 'Create Transaction'

  return (
    <>
      <Modal
        isOpen
        title={title}
        loading={isLoading}
        onOk={onSubmit}
        onClose={() => onClose(null)}
        titleOkay={item ? 'Submit' : 'Post Entry'}
        titleCancel="Cancel"
        disabled={!canSubmit || isLoading}
      >
        <div className="w-[min(900px,calc(100vw-4rem))]">
          {openedFromLedger && !item && (
            <p className="text-xs text-slate-500 mb-3 -mt-1">from General Ledger</p>
          )}

          <div className="relative grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
            {Object.keys(inputs).map((key) => {
              const input = inputs[key]
              if (key === 'payee') {
                input.additionalElements = (
                  <span className="btn-link" onClick={() => setPayeeModal(SettingKey.ACCOUNT_PAYEES)}>
                    Add Payee
                  </span>
                )
                ;(input as any).options = getOptionValues(payeeOptions)
              }
              const span = key === 'memo' ? 'col-span-2 md:col-span-4' : 'col-span-1'
              return (
                <div className={`input ${span}`} key={key}>
                  <RenderInput input={input} Key={key} onChange={onChange} />
                </div>
              )
            })}
          </div>

          <div className="border-t pt-4">
            <h3 className="text-sm font-semibold mb-2">Lines</h3>
            {lineError && (
              <p className="text-red-600 text-sm mb-2" role="alert">
                {lineError}
              </p>
            )}
            <div className="overflow-x-auto">
              <table className="w-full text-sm text-left">
                <thead className="text-xs text-gray-700 uppercase bg-gray-50">
                  <tr>
                    <th className="px-2 py-2 w-8">#</th>
                    <th className="px-2 py-2">Account *</th>
                    <th className="px-2 py-2 w-28">Debit</th>
                    <th className="px-2 py-2 w-28">Credit</th>
                    <th className="px-2 py-2">Description</th>
                    <th className="px-2 py-2 w-10"></th>
                  </tr>
                </thead>
                <tbody>
                  {lines.map((line, index) => (
                    <tr key={index} className="border-b">
                      <td className="px-2 py-1 text-gray-500">{index + 1}</td>
                      <td className="px-2 py-1">
                        <select
                          className="w-full border border-gray-300 rounded px-2 py-1 text-sm"
                          value={line.account}
                          onChange={(e) => onLineChange(index, 'account', e.target.value)}
                          disabled={amountsLocked}
                        >
                          <option value="">Select account…</option>
                          {Object.entries(accountOptions).map(([key, value]) => (
                            <option key={key} value={key}>
                              {value}
                            </option>
                          ))}
                        </select>
                      </td>
                      <td className="px-2 py-1">
                        <input
                          type="number"
                          className="w-full border border-gray-300 rounded px-2 py-1 text-sm text-right"
                          value={line.debit}
                          onChange={(e) => onLineChange(index, 'debit', e.target.value)}
                          disabled={amountsLocked}
                          placeholder="0.00"
                        />
                      </td>
                      <td className="px-2 py-1">
                        <input
                          type="number"
                          className="w-full border border-gray-300 rounded px-2 py-1 text-sm text-right"
                          value={line.credit}
                          onChange={(e) => onLineChange(index, 'credit', e.target.value)}
                          disabled={amountsLocked}
                          placeholder="0.00"
                        />
                      </td>
                      <td className="px-2 py-1">
                        <input
                          type="text"
                          className="w-full border border-gray-300 rounded px-2 py-1 text-sm"
                          value={line.description}
                          onChange={(e) => onLineChange(index, 'description', e.target.value)}
                        />
                      </td>
                      <td className="px-2 py-1">
                        {lines.length > 2 && (
                          <button
                            type="button"
                            className="text-red-500 hover:text-red-700 p-1"
                            onClick={() => removeLine(index)}
                          >
                            <TrashIcon className="w-4 h-4" />
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                  <tr className="bg-gray-50 font-semibold">
                    <td className="px-2 py-2"></td>
                    <td className="px-2 py-2 text-right">Total</td>
                    <td className="px-2 py-2 text-right">${thousandSeperator(totalDebit)}</td>
                    <td className="px-2 py-2 text-right">${thousandSeperator(totalCredit)}</td>
                    <td className="px-2 py-2" colSpan={2}></td>
                  </tr>
                </tbody>
              </table>
            </div>
            <div className="flex flex-wrap items-center justify-between gap-2 mt-3">
              <div className="flex gap-3">
                <span
                  className="flex items-center gap-1 text-shade-blue cursor-pointer hover:underline"
                  onClick={addLine}
                >
                  <PlusIcon className="w-4 h-4" />
                  Add line
                </span>
                <span className="text-gray-500 cursor-pointer hover:underline" onClick={clearAllLines}>
                  Clear all
                </span>
              </div>
              <div className={`text-sm font-medium ${balanced ? 'text-emerald-700' : 'text-amber-700'}`}>
                {balanced
                  ? `Balanced DR $${thousandSeperator(totalDebit)} = CR $${thousandSeperator(totalCredit)}`
                  : filledLines.length === 0
                  ? 'Enter lines to balance'
                  : `Out of balance by $${diff.toFixed(2)}`}
              </div>
            </div>
          </div>
        </div>
      </Modal>
      {payeeModal && (
        <OptionModal
          title="Update Payee options"
          settingKey={payeeModal}
          onClose={(values: IOptionType[]) => {
            values && dispatch(setGlobalConfig(GlobalConfigType.ACCOUNT_PAYEES, values))
            setPayeeModal(null)
          }}
          childLevel={1}
        />
      )}
    </>
  )
}
